import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit, Trash2, FolderKanban, Calendar, DollarSign, Users, Home } from "lucide-react";

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({
    name: "",
    client_id: "",
    status: "active",
    start_date: "",
    target_finish_date: "",
    budget: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, teamRes] = await Promise.all([
        API.get("/projects"),
        API.get("/clients"),
        API.get("/team"),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setTeam(teamRes.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        budget: parseFloat(form.budget) || 0,
      };

      if (editProject) {
        await API.put(`/projects/${editProject.project_id}`, data);
        toast.success("Project updated");
      } else {
        await API.post("/projects", data);
        toast.success("Project created");
      }
      setDialogOpen(false);
      setEditProject(null);
      setForm({ name: "", client_id: "", status: "active", start_date: "", target_finish_date: "", budget: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setEditProject(project);
    setForm({
      name: project.name,
      client_id: project.client_id,
      status: project.status,
      start_date: project.start_date || "",
      target_finish_date: project.target_finish_date || "",
      budget: project.budget?.toString() || "",
      notes: project.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await API.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.client_id === clientId);
    return client?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
      completed: "bg-blue-500/20 text-blue-400 border border-blue-500/30",
      on_hold: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
      cancelled: "bg-red-500/20 text-red-400 border border-red-500/30",
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide ${styles[status] || "bg-slate-800 text-slate-400 border border-slate-700"}`}>
        {status.replace("_", " ")}
      </span>
    );
  };

  if (loading) {
    return <div className="animate-pulse text-slate-400">Loading projects...</div>;
  }

  return (
    <div data-testid="projects-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">PROJECTS</h1>
          <p className="text-slate-500 font-ibm mt-1">Manage your construction projects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-project-btn"
              onClick={() => {
                setEditProject(null);
                setForm({ name: "", client_id: "", status: "active", start_date: "", target_finish_date: "", budget: "", notes: "" });
              }}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
            >
              <Plus size={18} className="mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="font-chivo text-white">{editProject ? "Edit Project" : "New Project"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-400">Project Name *</label>
                <Input
                  data-testid="project-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g., Kitchen Renovation"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Client *</label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="project-client-select" className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id} className="text-slate-200 hover:bg-slate-700">
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-400">Start Date</label>
                  <Input
                    type="date"
                    data-testid="project-start-date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-400">Target Finish</label>
                  <Input
                    type="date"
                    data-testid="project-end-date"
                    value={form.target_finish_date}
                    onChange={(e) => setForm({ ...form, target_finish_date: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-400">Budget ($)</label>
                  <Input
                    type="number"
                    data-testid="project-budget-input"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-400">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="project-status-select" className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="active" className="text-slate-200 hover:bg-slate-700">Active</SelectItem>
                      <SelectItem value="completed" className="text-slate-200 hover:bg-slate-700">Completed</SelectItem>
                      <SelectItem value="on_hold" className="text-slate-200 hover:bg-slate-700">On Hold</SelectItem>
                      <SelectItem value="cancelled" className="text-slate-200 hover:bg-slate-700">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Notes</label>
                <textarea
                  data-testid="project-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  rows={3}
                  placeholder="Project notes..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-project-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  {editProject ? "Update" : "Create"} Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Projects Grid - Tactical Dark */}
      {projects.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
          <FolderKanban size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No projects yet</h3>
          <p className="text-slate-500 mb-4">Create your first project to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.project_id}
              data-testid={`project-card-${project.project_id}`}
              className="bg-slate-900 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-chivo font-semibold text-lg text-white">{project.name}</h3>
                  <p className="text-sm text-slate-500">{getClientName(project.client_id)}</p>
                </div>
                {getStatusBadge(project.status)}
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Calendar size={16} className="text-slate-500" />
                  <span>
                    {project.start_date || "No start"} - {project.target_finish_date || "No end"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <DollarSign size={16} className="text-emerald-500" />
                  <span>Budget: ${project.budget?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Users size={16} className="text-slate-500" />
                  <span>{project.assigned_team?.length || 0} team members</span>
                </div>
              </div>

              {project.notes && (
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">{project.notes}</p>
              )}

              <div className="flex items-center gap-2 pt-4 border-t border-slate-800">
                <Button
                  size="sm"
                  data-testid={`view-project-${project.project_id}`}
                  onClick={() => navigate(`/projects/${project.project_id}/home`)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                >
                  <Home size={14} className="mr-1" />
                  Project Home
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`edit-project-${project.project_id}`}
                  onClick={() => handleEdit(project)}
                  className="border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                >
                  <Edit size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`delete-project-${project.project_id}`}
                  onClick={() => handleDelete(project.project_id)}
                  className="border-slate-700 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
